/**
 * Runtime HTTP injection seam for the Recall UI package.
 *
 * `packages/ui` is platform-agnostic and intentionally does NOT depend on
 * `@elva/platform` or `@tanstack/react-query` (it must also render in Storybook
 * with mock data). The host app owns the real HTTP transport (see
 * `apps/recall/src/data/http.ts` + `apps/recall/src/features/*`), so it injects
 * that transport here at bootstrap via `installRecallRuntime`.
 *
 * When no transport is installed, consumers (sequenceBuilderApi / useSaveSummary)
 * fall back to their mock implementations — preserving backward compatibility.
 */

export interface RecallRuntimeTransport {
  get<T = unknown>(path: string, params?: Record<string, unknown>): Promise<T>;
  post<T = unknown>(path: string, body?: unknown, params?: Record<string, unknown>): Promise<T>;
  patch<T = unknown>(path: string, body?: unknown, params?: Record<string, unknown>): Promise<T>;
  // Optional so older/partial transports keep working; the recall app installs both.
  put?<T = unknown>(path: string, body?: unknown, params?: Record<string, unknown>): Promise<T>;
  del?<T = unknown>(path: string, params?: Record<string, unknown>): Promise<T>;
}

/**
 * The transport is published on a well-known `globalThis` key. This avoids
 * needing a new `@elva/ui` package export (the public barrel `src/index.ts` is
 * intentionally not touched): the host app installs the transport by writing to
 * the same global key at bootstrap. In-package consumers read it back here.
 */
const RUNTIME_KEY = '__ELVA_RECALL_RUNTIME__';

type RuntimeGlobal = typeof globalThis & {
  [RUNTIME_KEY]?: RecallRuntimeTransport | null;
};

let transport: RecallRuntimeTransport | null = null;

/** Install the real backend transport. Call once at app bootstrap. */
export function installRecallRuntime(t: RecallRuntimeTransport): void {
  transport = t;
  (globalThis as RuntimeGlobal)[RUNTIME_KEY] = t;
}

/** Remove the installed transport (mainly for tests / Storybook). */
export function clearRecallRuntime(): void {
  transport = null;
  (globalThis as RuntimeGlobal)[RUNTIME_KEY] = null;
}

/** Returns the installed transport, or null when running on mocks. */
export function getRecallRuntime(): RecallRuntimeTransport | null {
  return transport ?? (globalThis as RuntimeGlobal)[RUNTIME_KEY] ?? null;
}

/** True when a real backend transport has been installed. */
export function hasRecallRuntime(): boolean {
  return getRecallRuntime() !== null;
}

/**
 * The host app can also publish the currently selected location id on a global
 * key so the in-package wizard location store seeds correctly. This avoids
 * needing to export the store from the @elva/ui barrel.
 */
const LOCATION_KEY = '__ELVA_RECALL_LOCATION_ID__';

type LocationGlobal = typeof globalThis & { [LOCATION_KEY]?: number | null };

export function setRecallLocationId(locationId: number | null | undefined): void {
  (globalThis as LocationGlobal)[LOCATION_KEY] = locationId ?? null;
}

export function getRecallLocationId(): number | null {
  return (globalThis as LocationGlobal)[LOCATION_KEY] ?? null;
}
